import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButton from '@mui/material/IconButton'
import { connect } from 'react-redux'

import { GiftIcon } from '@dhl/icons'
import { Box, useTheme } from '@dhl/theme'

import { WhatsNewDrawer } from './WhatsNewDrawer'
import { WhatsNewTooltip } from './WhatsNewTooltip'
import { ReduxState } from '~/redux/createRootReducer'
import { User } from '~/account/model/reducer'
import { getDocumentDate, usePrismicWhatsNewItems } from './prismic/usePrismicWhatsNewItems'
import { put } from '~/util/api/put'
import { getConfig } from '~/config'

type Props = {
  isLoggedIn: boolean | null
  user: User
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const LOCAL_STORAGE_KEY = 'whatsNewTooltipCloseDate'

const WhatsNewComponent: FC<Props> = ({ isLoggedIn, user }) => {
  const { prismic } = getConfig()
  const {
    device: { isDesktop },
  } = useTheme()
  const { i18n, t } = useTranslation()
  const lang = i18n.language.toLowerCase().replace('_', '-')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const { data: results = [] } = usePrismicWhatsNewItems({
    url: prismic.endpoint,
    lang,
  })
  const [hasNewDocuments, setHasNewDocuments] = useState(false)
  const latestDocumentReadDate =
    isLoggedIn && user.timeLastNewsRead ? new Date(user.timeLastNewsRead).toISOString() : null
  const latestDocument = results[0]
  const latestDocumentDate = getDocumentDate(latestDocument)

  // Set tooltip open based on: last closed date, last read news, availability of new documents
  useEffect(() => {
    const latestTooltipCloseDate = localStorage.getItem(LOCAL_STORAGE_KEY)
    const isLatestTooltipClosed =
      latestDocumentDate && latestTooltipCloseDate && latestDocumentDate <= latestTooltipCloseDate
    const shouldOpenTooltip = results.length > 0 && hasNewDocuments && !isLatestTooltipClosed
    const hasNew =
      results.length > 0 &&
      (latestDocumentReadDate === null || (latestDocumentDate && latestDocumentDate > latestDocumentReadDate))
    setHasNewDocuments(!!hasNew)
    setIsTooltipOpen(shouldOpenTooltip)
  }, [results, hasNewDocuments, latestDocumentDate, latestDocumentReadDate])

  const storeLastNewsRead = async () => {
    await put('/api/user/last-news-read', {
      timestamp: latestDocumentDate,
    }).then(() => {
      setHasNewDocuments(false)
    })
  }

  const openDrawer = () => {
    if (hasNewDocuments && isLoggedIn) {
      storeLastNewsRead()
    }
    setIsDrawerOpen(true)
    setIsTooltipOpen(false)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
  }

  const handleTooltipClose = () => {
    if (latestDocumentDate) {
      localStorage.setItem(LOCAL_STORAGE_KEY, latestDocumentDate)
    }
    setIsTooltipOpen(false)
  }

  return isDesktop ? (
    <>
      <WhatsNewTooltip
        open={isTooltipOpen}
        document={latestDocument}
        onReadMoreClick={openDrawer}
        onCloseClick={handleTooltipClose}
      >
        <IconButton
          title={t('widgets_release_notes_title')}
          onClick={openDrawer}
          size="large"
          sx={{
            marginRight: 0.5,
          }}
        >
          <GiftIcon
            data-test="whats-new-icon"
            sx={{
              color: 'common.black',
              paddingBottom: 0.25,
            }}
          />
          {hasNewDocuments && isLoggedIn && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: 3,
                backgroundColor: 'primary.main',
                position: 'absolute',
                top: 7,
                right: 7,
              }}
            />
          )}
        </IconButton>
      </WhatsNewTooltip>
      <WhatsNewDrawer documents={results} isOpen={isDrawerOpen} onClose={handleDrawerClose} />
    </>
  ) : null
}

const mapStateToProps = (state: ReduxState) => ({
  isLoggedIn: state.account.isLoggedIn,
  user: state.account.user,
})

export const WhatsNewButton = connect(mapStateToProps)(WhatsNewComponent)
